import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ═══════════════════════════════════════════
   HERO — Full viewport, left-aligned text at bottom
   ═══════════════════════════════════════════ */

export const DetailHero = styled.section<{ $bgImage?: string }>`
  min-height: 100vh;
  background-image:
    linear-gradient(180deg,
      rgba(10, 22, 40, 0.0) 0%,
      rgba(10, 22, 40, 0.1) 35%,
      rgba(10, 22, 40, 0.7) 75%,
      var(--bg-primary) 100%),
    url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center 30%;
  background-attachment: fixed;
  display: flex;
  align-items: flex-end;
  padding: 0 1.5rem 5rem;

  @media (max-width: 768px) {
    background-attachment: scroll;
    min-height: 85vh;
    padding-bottom: 3rem;
    background-position: center 20%;
  }
`;

export const HeroContent = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  width: 100%;
  animation: ${fadeInUp} 0.9s ease-out;
`;

export const HeroBadge = styled.div`
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--accent-teal);
  background: rgba(10, 22, 40, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 201, 177, 0.3);
  padding: 0.5rem 1.4rem;
  border-radius: 100px;
  margin-bottom: 1.2rem;
`;

export const HeroH1 = styled.h1`
  font-family: var(--font-heading);
  font-size: clamp(2.2rem, 5vw, 3.6rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 0.75rem;
  text-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);
  max-width: 700px;
  line-height: 1.15;
`;

export const HeroTagline = styled.p`
  font-size: clamp(1rem, 1.5vw, 1.15rem);
  color: var(--text-secondary);
  max-width: 550px;
  line-height: 1.7;
  margin-bottom: 2rem;
`;

export const HeroCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-gold);
  color: var(--text-inverse);
  padding: 0.9rem 2.2rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all var(--transition-base);
  box-shadow: var(--shadow-glow-gold);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 30px rgba(244, 170, 0, 0.4);
    color: var(--text-inverse);
  }
`;

/* ═══════════════════════════════════════════
   SHARED SECTION COMPONENTS
   ═══════════════════════════════════════════ */

export const SectionEyebrow = styled.div`
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--accent-teal);
  margin-bottom: 0.75rem;
`;

export const SectionTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 3px;
    background: var(--gradient-accent);
    border-radius: 2px;
  }
`;

export const ContentSection = styled.section`
  padding: 5rem 0;
  background: var(--bg-primary);
`;

export const Container = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

/* ═══════════════════════════════════════════
   SECTION 2: THE EXPERIENCE — Multi-paragraph content
   ═══════════════════════════════════════════ */

export const ExperienceBody = styled.div`
  max-width: 780px;
  margin-top: 2rem;
`;

export const ExperienceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 500px;
  gap: 3rem;
  margin-top: 2rem;
  align-items: start;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const ExperienceMapWrapper = styled.div`
  img {
    width: 100%;
    border-radius: var(--border-radius-lg);
    border: 1px solid var(--border-color);
    box-shadow: var(--shadow-md);
  }

  @media (max-width: 1000px) {
    max-width: 520px;
  }
`;

export const ExperienceParagraph = styled.p`
  font-size: 1.05rem;
  line-height: 1.9;
  color: var(--text-secondary);
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

/* ═══════════════════════════════════════════
   SECTION 3: TARGET SPECIES — Horizontal layout cards
   ═══════════════════════════════════════════ */

export const SpeciesSection = styled.section`
  padding: 5rem 0;
  background: var(--bg-secondary);
`;

export const SpeciesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 2.5rem;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

export const SpeciesRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: rgba(19, 39, 66, 0.6);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: var(--border-hover);
  }
`;

export const SpeciesName = styled.span`
  font-family: var(--font-heading);
  font-size: 1rem;
  font-weight: 700;
  color: var(--accent-teal);
  letter-spacing: 0.3px;
`;

export const SeasonBadge = styled.span`
  font-family: var(--font-heading);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent-gold);
  background: rgba(244, 170, 0, 0.1);
  border: 1px solid rgba(244, 170, 0, 0.2);
  padding: 0.25rem 0.65rem;
  border-radius: 100px;
  white-space: nowrap;
`;

/* ═══════════════════════════════════════════
   SECTION 4: TRIP OPTIONS — Horizontal bar/strip layout
   ═══════════════════════════════════════════ */

export const TripOptionsSection = styled.section`
  padding: 5rem 0;
  background: var(--bg-primary);
`;

export const TripStrip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 2.5rem;
`;

export const TripRow = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-base);
  text-decoration: none;

  &:hover {
    border-color: rgba(244, 170, 0, 0.3);
    transform: translateY(-2px);
    box-shadow: var(--shadow-glow-gold);
  }

  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 0.75rem;
  }
`;

export const TripName = styled.span`
  font-family: var(--font-heading);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-primary);
  flex: 1;
`;

export const TripDuration = styled.span`
  font-size: 0.9rem;
  color: var(--text-muted);
  flex: 0 0 auto;
  margin: 0 2rem;

  @media (max-width: 600px) {
    margin: 0;
  }
`;

export const TripPrice = styled.span`
  font-family: var(--font-heading);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: var(--gradient-gold);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  flex: 0 0 auto;
`;

/* ═══════════════════════════════════════════
   SECTION 5: INCLUDED / TO BRING — Two-column checklist
   ═══════════════════════════════════════════ */

export const ChecklistSection = styled.section`
  padding: 5rem 0;
  background: var(--bg-secondary);
`;

export const ChecklistColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-top: 2.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

export const ChecklistGroup = styled.div``;

export const ChecklistHeading = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 1.25rem;
  letter-spacing: 0.5px;
`;

export const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);

  &:last-child {
    border-bottom: none;
  }
`;

export const CheckIcon = styled.span`
  color: var(--accent-teal);
  flex-shrink: 0;
  margin-top: 0.15rem;
  font-size: 0.8rem;
`;

export const BulletIcon = styled.span`
  color: var(--text-muted);
  flex-shrink: 0;
  margin-top: 0.15rem;
  font-size: 0.8rem;
`;

export const ChecklistText = styled.span`
  font-size: 0.95rem;
  color: var(--text-secondary);
  line-height: 1.6;
`;

/* ═══════════════════════════════════════════
   SECTION 6: LOGISTICS — Horizontal icon+text pairs
   ═══════════════════════════════════════════ */

export const LogisticsSection = styled.section`
  padding: 5rem 0;
  background: var(--bg-primary);
`;

export const LogisticsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  margin-top: 2.5rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const LogisticsItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  border-right: 1px solid var(--border-color);

  &:nth-child(even) {
    border-right: none;
  }

  &:nth-last-child(-n+2) {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    border-right: none;

    &:last-child {
      border-bottom: none;
    }

    &:nth-last-child(-n+2) {
      border-bottom: 1px solid var(--border-color);
    }

    &:nth-last-child(1) {
      border-bottom: none;
    }
  }
`;

export const LogisticsIcon = styled.span`
  color: var(--accent-teal);
  font-size: 1.1rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
`;

export const LogisticsInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

export const LogisticsLabel = styled.span`
  font-family: var(--font-heading);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--accent-teal);
  margin-bottom: 0.35rem;
`;

export const LogisticsValue = styled.span`
  font-size: 0.95rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

/* ═══════════════════════════════════════════
   SECTION 7: UNIQUE DETAILS — Bullet list with teal checks
   ═══════════════════════════════════════════ */

export const UniqueSection = styled.section`
  padding: 5rem 0;
  background: var(--bg-secondary);
`;

export const UniqueList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 3rem;
  margin-top: 2.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.2rem;
  }
`;

export const UniqueItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.65rem 0;
`;

export const UniqueIcon = styled.span`
  color: var(--accent-teal);
  flex-shrink: 0;
  margin-top: 0.2rem;
  font-size: 0.85rem;
`;

export const UniqueText = styled.span`
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.6;
`;

/* ═══════════════════════════════════════════
   SECTION 8: CTA — Book This Charter
   ═══════════════════════════════════════════ */

export const CtaSection = styled.section<{ $bgImage?: string }>`
  padding: 6rem 0;
  background-image:
    linear-gradient(135deg, rgba(10, 22, 40, 0.88) 0%, rgba(15, 31, 53, 0.82) 100%),
    url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center 20%;
  background-attachment: fixed;
  text-align: center;
  position: relative;

  @media (max-width: 768px) {
    background-attachment: scroll;
    padding: 4rem 0;
    background-position: center 30%;
  }

  &::before, &::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--accent-gold), transparent);
    opacity: 0.25;
  }
  &::before { top: 0; }
  &::after { bottom: 0; }
`;

export const CtaInner = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const CtaTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 800;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

export const CtaSubtext = styled.p`
  font-size: 1.05rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.7;
`;

export const CtaButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-gold);
  color: var(--text-inverse);
  padding: 1rem 2.5rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all var(--transition-base);
  box-shadow: var(--shadow-glow-gold);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(244, 170, 0, 0.4);
    color: var(--text-inverse);
  }
`;
